/**
 * A header button: an icon and a name. In a collapsed IconTextRow the name is
 * hidden (CSS) and lives on as the aria-label; `title` is the tooltip. `title` never
 * shows on a touch screen, so a long press (450 ms) shows the name in a bubble
 * under the button instead; a short tap is an ordinary click.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './designerHeader.css';

const LONG_PRESS_MS = 450;
const MOVE_PX = 8;

export interface IconTextButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  icon: React.ReactNode;
  label: string;
  /** The tooltip; the label when absent. */
  title?: string;
  /** Pressed / chosen: amber outline (also sets aria-pressed unless given). */
  active?: boolean;
  /** The row's main action: solid amber. */
  accent?: boolean;
  /** No box until hovered. */
  quiet?: boolean;
  /** Never show the label, whatever the row's room. */
  iconOnly?: boolean;
}

export const IconTextButton = React.forwardRef<HTMLButtonElement, IconTextButtonProps>(({
  icon, label, title, active, accent, quiet, iconOnly, className, children, onPointerDown, onPointerUp, onPointerCancel, onPointerMove, onContextMenu, ...rest
}, ref) => {
  const tip = title ?? label;
  const inner = useRef<HTMLButtonElement | null>(null);
  const setRefs = useCallback((el: HTMLButtonElement | null) => {
    inner.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  }, [ref]);

  // The long-press tooltip (touch only).
  const [tipAt, setTipAt] = useState<{ x: number; y: number } | null>(null);
  const press = useRef<{ x: number; y: number; timer: number } | null>(null);
  const clearPress = useCallback(() => {
    if (press.current) { window.clearTimeout(press.current.timer); press.current = null; }
  }, []);
  useEffect(() => () => clearPress(), [clearPress]);
  useEffect(() => {
    if (!tipAt) return;
    const hide = () => setTipAt(null);
    const timer = window.setTimeout(hide, 1800);
    window.addEventListener('pointerdown', hide, true);
    return () => { window.clearTimeout(timer); window.removeEventListener('pointerdown', hide, true); };
  }, [tipAt]);

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    onPointerDown?.(e);
    if (e.pointerType !== 'touch' || e.defaultPrevented) return;
    clearPress();
    const timer = window.setTimeout(() => {
      press.current = null;
      const rect = inner.current?.getBoundingClientRect();
      if (rect) setTipAt({ x: rect.left + rect.width / 2, y: rect.bottom + 6 });
    }, LONG_PRESS_MS);
    press.current = { x: e.clientX, y: e.clientY, timer };
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    onPointerMove?.(e);
    if (press.current && Math.hypot(e.clientX - press.current.x, e.clientY - press.current.y) > MOVE_PX) clearPress();
  };
  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => { onPointerUp?.(e); clearPress(); };
  const handlePointerCancel = (e: React.PointerEvent<HTMLButtonElement>) => { onPointerCancel?.(e); clearPress(); };
  const handleContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    onContextMenu?.(e);
    if (tipAt || press.current) e.preventDefault(); // a long press is not a context menu here
  };

  const classes = ['dh-btn', active ? 'is-active' : '', accent ? 'is-accent' : '', quiet ? 'is-quiet' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <>
      <button
        ref={setRefs}
        type="button"
        className={classes}
        title={tip}
        aria-label={label}
        aria-pressed={rest['aria-pressed'] ?? (active !== undefined ? active : undefined)}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onContextMenu={handleContextMenu}
        {...rest}
      >
        {icon}
        {!iconOnly && <span className="dh-label">{label}</span>}
        {children}
      </button>
      {tipAt && createPortal(
        <span role="tooltip" className="dh-tip" style={{ left: tipAt.x, top: tipAt.y, transform: 'translateX(-50%)' }}>{tip}</span>,
        document.body,
      )}
    </>
  );
});
IconTextButton.displayName = 'IconTextButton';

export default IconTextButton;
